import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { useClientPath } from '../lib/clientLink.js';
import StatusChip from '../components/StatusChip.jsx';
import PostReview from '../components/PostReview.jsx';
import { DeadlineNote, AutoApprovedNote, HowItWorks, PLAN_EXPLAINER_KEY } from '../components/ApprovalBits.jsx';
import { ReplaceControl, ReplaceState, ReplaceReview } from '../components/ReplaceBits.jsx';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// Session 18: FLAGSHIP -> Flagship.
const titleCase = (s) => String(s || '').toLowerCase().replace(/(^|[\s_-])([a-z])/g, (_m, a, b) => `${a === '_' ? ' ' : a}${b.toUpperCase()}`);
const stop = (e) => e.stopPropagation();

// Session 18: "Why this post", collapsed by default. The client reads
// client_summary (else why_now); the agency sees both.
function WhyThisPost({ row, isAgency, open, onToggle }) {
  const why = row.why_now || '';
  const summary = row.client_summary || '';
  const body = isAgency ? (why || summary) : (summary || why);
  if (!body) return null;
  return (
    <div className="plan-why" onClick={stop} onKeyDown={stop}>
      <button type="button" className="link-btn plan-why-toggle" aria-expanded={open} onClick={onToggle}>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span> Why this post
      </button>
      {open && (
        <div className="plan-why-body">
          {isAgency ? (
            <>
              {why && <p>{why}</p>}
              {summary && <p><span className="plan-why-label">What the client reads:</span> {summary}</p>}
            </>
          ) : <p>{body}</p>}
        </div>
      )}
    </div>
  );
}

function routeFor(row) {
  if (row.status === 'Published') return `/blogs/${row.slug}`;
  if (row.status === 'Ready')     return `/blogs/${row.slug}`;
  if (row.status === 'Awaiting your input') return `/blogs/${row.slug}/review`;
  if (row.status === 'Waiting on client')   return `/blogs/${row.slug}/review`;   // Session 8: agency wording, read-only review page
  // 'Publishing…' (Session 8): no link until the live URL exists.
  return null;
}

// Session 5 Part D: a plan waiting on the client, shown above the schedule.
// Session 20: per-post Approve / Replace (PostReview), saved as you go; the
// bottom button sends only the replacements, or approves the plan. The agency
// sees the client's decisions so far, read-only.
function PendingPlan({ activeId }) {
  const { isAgency } = useAuth();
  // Session 21: on Digital Lotus's own plan the agency reviews as the client.
  const dlOwner = isAgency && activeId === 'digitallotus';
  const [plan, setPlan] = useState(null);
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPlan(null); setBanner(null);
      if (!activeId) return;
      const { data } = await supabase.from('v_portal_plan_jobs').select('*')
        .eq('client_id', activeId).eq('status', 'sent').order('created_at', { ascending: false }).limit(1);
      if (!cancelled) setPlan(data?.[0] || false);
    })();
    return () => { cancelled = true; };
  }, [activeId]);

  if (!plan) return banner ? <div className={`banner banner-${banner.tone}`}>{banner.text}</div> : null;

  const posts = plan.posts || [];
  const round = plan.review_round || 0;
  const newCount = round > 0 ? posts.filter((p) => plan.post_reviews?.[p.slug]?.round === round).length : 0;

  function submitted(_res, { replacements }) {
    setBanner({ tone: 'ok', text: replacements > 0
      ? `Got it. We'll replace ${replacements} post${replacements === 1 ? '' : 's'} and send ${replacements === 1 ? 'it' : 'them'} back to you. Everything you approved stays approved.`
      : 'Thanks, your plan is approved. We\'ll send a few questions ahead of each post.' });
    setPlan(false);
  }

  return (
    <section className="review-section">
      <h2>{newCount > 0 ? `Your updated plan: ${newCount} new post${newCount === 1 ? '' : 's'} to review` : 'Your new plan is ready for review'}</h2>
      <p className="lede" style={{ marginBottom: 16 }}>
        {newCount > 0
          ? 'Everything you already approved stays approved. Only the new posts need a decision.'
          : `${posts.length} posts. Each one answers something your customers search for and points them to the right page on your site.`}
        {(!isAgency || dlOwner) && ' Approve or replace each post. Your choices are saved as you go.'}
      </p>
      {plan.auto_approves && <DeadlineNote at={plan.deadline_at} agency={isAgency} />}
      {isAgency && !dlOwner && <p className="field-hint" style={{ marginTop: 0 }}>Waiting on the client's approval. Their choices so far:</p>}
      <PostReview
        plan={{ ...plan, posts: posts.map((p) => ({ ...p, why: p.client_summary })) }}
        stage="client"
        readOnly={isAgency && !dlOwner}
        onSubmitted={submitted}
        clientId={activeId}
      />
    </section>
  );
}

// Session 13: after an automatic approval the client sees one quiet line.
function AutoApprovedPlan({ activeId }) {
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPlan(null);
      if (!activeId) return;
      const { data } = await supabase.from('v_portal_plan_jobs').select('job_id, auto_approved_at')
        .eq('client_id', activeId).eq('status', 'approved').not('auto_approved_at', 'is', null)
        .order('auto_approved_at', { ascending: false }).limit(1);
      if (!cancelled) setPlan(data?.[0] || null);
    })();
    return () => { cancelled = true; };
  }, [activeId]);
  return plan ? <AutoApprovedNote at={plan.auto_approved_at} /> : null;
}

export default function Plan() {
  const { activeId, activeClient, isAgency, me, clients } = useAuth();
  const [explainerGone, setExplainerGone] = useState(false);
  // Session 13: "How this works", once per client user until dismissed.
  const mode = me?.approval_mode || clients?.find((c) => c.client_id === activeId)?.approval_mode || 'client';
  const explainerDismissed = explainerGone || (me?.explainers_dismissed || []).includes(PLAN_EXPLAINER_KEY);
  const navigate = useNavigate();
  const withClient = useClientPath();
  const [rows,    setRows]    = useState(null);
  const [error,   setError]   = useState(null);
  const [reps,    setReps]    = useState({});      // Session 16: open replacements by slug
  const [note,    setNote]    = useState(null);    // Session 16: Replace confirmation
  const [tick,    setTick]    = useState(0);
  const [whyOpen, setWhyOpen] = useState({});     // Session 18: open "Why this post" by slug

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      if (!activeId) { setRows(null); return; }
      const [{ data, error }, { data: rd }] = await Promise.all([
        supabase.from('v_portal_plan').select('*').eq('client_id', activeId).order('publish_date', { ascending: true }),
        supabase.from('v_portal_replacements').select('*').eq('client_id', activeId)
          .in('status', ['queued', 'running', 'agency_review', 'sent']),
      ]);
      if (cancelled) return;
      if (error) setError(error.message);
      else       setRows(data || []);
      setReps(Object.fromEntries((rd || []).map((r) => [r.slug, r])));
    })();
    return () => { cancelled = true; };
  }, [activeId, tick]);
  useEffect(() => { setRows(null); setNote(null); setReps({}); setWhyOpen({}); }, [activeId]);
  const agencyOnly = mode === 'agency_only';
  const replaced = (text) => { setNote(text); setTick((t) => t + 1); };

  if (rows === null && !error) return <p className="lede">Loading your plan…</p>;

  return (
    <>
      <h1>Your plan</h1>
      <p className="lede">
        Here's what's planned for {activeClient?.name || 'your business'}.
        We'll keep the status current as posts move through the pipeline.
        {' '}<Link to={withClient('/blogs')}>See every finished post →</Link>
      </p>

      {!isAgency && (
        <HowItWorks mode={mode} days={me?.approval_deadline_days} dismissed={explainerDismissed}
                    onDismissed={() => setExplainerGone(true)} />
      )}

      <PendingPlan activeId={activeId} />
      <AutoApprovedPlan activeId={activeId} />

      {error && <div className="empty"><h2>Couldn't load the plan</h2><p>{error}</p></div>}
      {note && <div className="banner banner-ok">{note}</div>}

      {!error && rows && rows.length === 0 && (
        <div className="empty">
          <h2>Your first posts are being planned</h2>
          <p>We'll show them here as soon as they're on the schedule.</p>
        </div>
      )}

      {!error && rows && rows.length > 0 && rows.some((r) => r.why_now || r.client_summary) && (() => {
        const allOpen = rows.every((r) => !(r.why_now || r.client_summary) || whyOpen[r.slug]);
        return (
          <div className="plan-tools">
            <button type="button" className="link-btn" aria-pressed={allOpen}
                    onClick={() => setWhyOpen(allOpen ? {} : Object.fromEntries(rows.map((r) => [r.slug, true])))}>
              {allOpen ? 'Collapse all' : 'Expand all'}
            </button>
          </div>
        );
      })()}

      {!error && rows && rows.length > 0 && (
        <div className="card" role="table" aria-label="Quarterly plan">
          {rows.map((r) => {
            const target = routeFor(r) && withClient(routeFor(r));
            const rep = reps[r.slug];
            return (
              <div key={r.slug}>
                <div
                  className={`plan-row plan-row-detail ${target ? 'clickable' : ''}`}
                  role={target ? 'link' : 'row'}
                  tabIndex={target ? 0 : -1}
                  onClick={() => target && navigate(target)}
                  onKeyDown={(e) => target && (e.key === 'Enter' || e.key === ' ') && navigate(target)}
                >
                  {/* Session 18: date, title, status, Replaced; then keyword / type / page, excerpt, why */}
                  <div className="plan-row-head">
                    <div className="date" role="cell">{formatDate(r.publish_date)}</div>
                    <div className="title" role="cell">
                      {r.title}{r.replaced && <span className="mode-tag">Replaced</span>}
                    </div>
                    <div className="status-cell" role="cell"><StatusChip status={r.status} /></div>
                  </div>
                  {(r.primary_keyword || r.post_type || r.money_page) && (
                    <div className="plan-row-meta">
                      {r.primary_keyword && <span>{r.primary_keyword}</span>}
                      {r.post_type && <span>{titleCase(r.post_type)}</span>}
                      {r.money_page && <span>Links to {r.money_page}</span>}
                    </div>
                  )}
                  {r.excerpt && <p className="plan-row-excerpt">{r.excerpt}</p>}
                  <WhyThisPost row={r} isAgency={isAgency} open={!!whyOpen[r.slug]}
                               onToggle={() => setWhyOpen((o) => ({ ...o, [r.slug]: !o[r.slug] }))} />
                  <div className="plan-row-actions">
                    <ReplaceState row={r} isAgency={isAgency} />
                    {/* Session 16: Replace while the questions haven't gone out */}
                    <ReplaceControl row={r} isAgency={isAgency} agencyOnly={agencyOnly} onDone={replaced} />
                  </div>
                </div>
                {rep && <ReplaceReview rep={rep} isAgency={isAgency} onDone={replaced} old={r} />}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
